/**
 * Единый источник непрочитанных уведомлений.
 *
 * До этого их читали два независимых поллинга — колокольчик и левый рельс, —
 * и оба сами разбирали `payload`. Доске нужен третий срез (по записи), поэтому
 * агрегация вынесена сюда: один запрос раз в 60 секунд, три готовых среза.
 *
 * Связь уведомления с записью живёт ТОЛЬКО в `payload.itemId` — в БД такой
 * колонки нет, в типах она `Record<string, unknown>`. Поэтому разбор payload
 * собран в одном месте, а не размазан по компонентам.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { Notification } from '@lens/shared';
import { listNotifications, markNotificationsRead } from './api.ts';

/** Что показать в подсказке у подсвеченной записи. */
const KIND_HINTS: Record<string, string> = {
  mention: 'Вас упомянули',
  assignment: 'Вам назначили',
  reply: 'Вам ответили',
};

export interface ItemNotice {
  /** id уведомлений по этой записи — ими же помечаем прочитанными. */
  ids: string[];
  /** Человекочитаемая причина: «Вас упомянули», «Вам назначили». */
  hints: string[];
}

interface NotificationsValue {
  /** spaceId → сколько непрочитанных (бейдж пространства в рельсе). */
  unreadBySpace: Record<string, number>;
  /** itemId → непрочитанные по записи (подсветка на доске и в таблице). */
  unreadByItem: Record<string, ItemNotice>;
  /** Всего непрочитанных. */
  unreadCount: number;
  /** Перечитать с сервера (после пометки прочитанным). */
  reload: () => void;
  /** Пометить прочитанными все уведомления по записи. Без непрочитанных — no-op. */
  markItemRead: (itemId: string) => Promise<void>;
}

const NotificationsContext = createContext<NotificationsValue>({
  unreadBySpace: {},
  unreadByItem: {},
  unreadCount: 0,
  reload: () => {},
  markItemRead: async () => {},
});

function payloadOf(n: Notification): Record<string, unknown> {
  return (n.payload as Record<string, unknown> | null) ?? {};
}

export function NotificationsProvider({
  children,
  enabled = true,
}: {
  children: ReactNode;
  /** false — пользователь не залогинен: не опрашиваем и держим пустое состояние. */
  enabled?: boolean;
}): JSX.Element {
  const [unread, setUnread] = useState<Notification[]>([]);

  const reload = useCallback((): void => {
    if (!enabled) {
      setUnread([]);
      return;
    }
    void listNotifications(true)
      .then((res) => setUnread(res.notifications))
      .catch(() => {
        /* сеть/401 — оставляем прошлое состояние, индикаторы не мигают */
      });
  }, [enabled]);

  useEffect(() => {
    reload();
    if (!enabled) return;
    const iv = setInterval(reload, 60_000);
    return () => clearInterval(iv);
  }, [reload, enabled]);

  const { unreadBySpace, unreadByItem } = useMemo(() => {
    const bySpace: Record<string, number> = {};
    const byItem: Record<string, ItemNotice> = {};
    for (const n of unread) {
      const payload = payloadOf(n);
      const spaceId = payload.spaceId;
      if (typeof spaceId === 'string') bySpace[spaceId] = (bySpace[spaceId] ?? 0) + 1;
      // itemId есть не всегда: упоминание бывает в комментарии без записи.
      const itemId = payload.itemId;
      if (typeof itemId !== 'string') continue;
      const notice = byItem[itemId] ?? { ids: [], hints: [] };
      notice.ids.push(n.id);
      const hint = KIND_HINTS[n.kind];
      if (hint && !notice.hints.includes(hint)) notice.hints.push(hint);
      byItem[itemId] = notice;
    }
    return { unreadBySpace: bySpace, unreadByItem: byItem };
  }, [unread]);

  const markItemRead = useCallback(
    async (itemId: string): Promise<void> => {
      const ids = unreadByItem[itemId]?.ids;
      if (!ids || ids.length === 0) return;
      // Гасим подсветку сразу, не дожидаясь ответа: иначе метка живёт ещё
      // секунду после открытия записи и выглядит как «не сработало».
      setUnread((prev) => prev.filter((n) => !ids.includes(n.id)));
      try {
        await markNotificationsRead({ ids });
      } catch {
        // Не удалось — вернём как было следующим поллингом.
        reload();
      }
    },
    [unreadByItem, reload],
  );

  const value = useMemo(
    () => ({ unreadBySpace, unreadByItem, unreadCount: unread.length, reload, markItemRead }),
    [unreadBySpace, unreadByItem, unread.length, reload, markItemRead],
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}

export function useNotifications(): NotificationsValue {
  return useContext(NotificationsContext);
}
